import { useRef, useState } from 'react';

import { useReactToPrint } from 'react-to-print';

/**
 * 문서 미리보기 로직 관리 커스텀 훅
 * - react-to-print를 사용하여 인쇄 기능 제공
 *
 * [인쇄 기능 구현 방법]
 * 1. useRef로 인쇄할 컴포넌트에 전달할 ref 생성
 * 2. useReactToPrint 훅 사용 (window.print() 대신)
 *    - 장점: 전역 레이아웃(헤더, 사이드바 등) 자동 숨김 처리
 *    - 장점: 컴포넌트만 선택적으로 인쇄 가능
 * 3. onBeforeGetContent에서 미리보기 모드 확인 및 전환
 *    - 인쇄 전에 미리보기 모드로 전환하여 최신 데이터 반영
 *
 * [다른 컴포넌트에서 사용 시]
 * - 동일한 패턴으로 useRef + useReactToPrint 조합 사용
 * - 인쇄할 컴포넌트는 forwardRef로 래핑 필요
 *
 * @returns {Object} 미리보기 상태, ref, 핸들러
 */
function useDocumentPreview() {
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  // 인쇄할 컴포넌트에 전달할 ref (forwardRef로 래핑된 컴포넌트에 연결)
  const printContentRef = useRef(null);

  function handlePreview() {
    setIsPreviewMode(true);
    // 미리보기 모드에서는 스크롤을 문서 시작 부분으로 이동
    setTimeout(function () {
      window.scrollTo(0, 0);
    }, 100);
  }

  function handleClosePreview() {
    setIsPreviewMode(false);
  }

  /**
   * react-to-print를 사용한 인쇄 핸들러
   * - window.print() 대신 react-to-print 사용 이유:
   *   1. 전역 레이아웃(헤더, 사이드바, 푸터 등) 자동 숨김
   *   2. 특정 컴포넌트만 선택적으로 인쇄 가능
   *   3. 인쇄 전 콜백(onBeforeGetContent)으로 상태 동기화 가능
   *
   * [다른 컴포넌트에서 사용 시]
   * - contentRef: 인쇄할 컴포넌트의 ref
   * - documentTitle: 인쇄 대화상자에 표시될 문서 제목
   * - onBeforeGetContent: 인쇄 전 실행할 비동기 함수 (Promise 반환 가능)
   */
  const handlePrint = useReactToPrint({
    contentRef: printContentRef,
    documentTitle: '전자결재 문서',
    onBeforeGetContent: function () {
      // 미리보기 모드가 아닌 경우 미리보기 모드로 전환
      // 인쇄 전에 최신 데이터가 반영되도록 보장
      if (!isPreviewMode) {
        handlePreview();
        // 미리보기 모드로 전환 후 약간의 지연을 두고 인쇄
        // (상태 업데이트 및 렌더링 완료 대기)
        return new Promise(function (resolve) {
          setTimeout(function () {
            resolve();
          }, 300);
        });
      }
      return Promise.resolve();
    },
  });

  return {
    isPreviewMode,
    printContentRef,
    handlePreview,
    handleClosePreview,
    handlePrint,
  };
}

export default useDocumentPreview;
